import type { EventSubscription } from "expo-modules-core";

import ExpoAndroidAuto from "$modules/expo-android-auto";
import CustomPlatform from "$src/core/utils/platform/platform";

import type {
  CarCatalog,
  CarConnectionState,
  CarNativeModule,
  CarPlaybackDescriptor,
  CarPlaybackError,
  CarPlayRequest,
  CarSearchRequest,
  CarSearchResults,
} from "./types";

// Android only: iOS CarPlay is driven by the JS car task via `expo-car`.
const nativeModule = CustomPlatform.select<CarNativeModule | null>({
  android: ExpoAndroidAuto,
  default: null,
});

const noopUnsubscribe: EventSubscription = {
  remove: () => {
    // noop
  },
};

/** Expo's native `Map<String, Any?>` conversion rejects JS `undefined`; omit those fields. */
export const stripUndefined = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((entry) => stripUndefined(entry)) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, stripUndefined(entry)]),
    ) as T;
  }
  return value;
};

/** Native argument-conversion errors throw *synchronously*, escaping callers' `.catch(...)`; wrap them into rejections. */
export const invoke = <T = void>(
  call: () => Promise<T> | undefined,
  fallback?: T,
): Promise<T> => {
  try {
    return call() ?? Promise.resolve(fallback as T);
  } catch (error) {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    );
  }
};

/** Every method no-ops on unsupported platforms, so callers need no guards. */
const Car = {
  isSupported: nativeModule !== null,

  setCatalog: (catalog: CarCatalog): Promise<void> =>
    invoke(() => nativeModule?.setCatalogAsync(stripUndefined(catalog))),

  setSearchResults: (results: CarSearchResults): Promise<void> =>
    invoke(() => nativeModule?.setSearchResultsAsync(stripUndefined(results))),

  setPlayback: (playback: CarPlaybackDescriptor | null): Promise<void> =>
    invoke(() => nativeModule?.setPlaybackAsync(stripUndefined(playback))),

  consumePendingPlay: (): Promise<CarPlayRequest | null> =>
    invoke(() => nativeModule?.consumePendingPlayAsync(), null),

  isConnected: (): boolean => nativeModule?.isConnected() ?? false,

  addConnectedListener: (
    listener: (event: CarConnectionState) => void,
  ): EventSubscription =>
    nativeModule?.addListener("onConnectedChange", listener) ?? noopUnsubscribe,

  addPlayRequestListener: (
    listener: (event: CarPlayRequest) => void,
  ): EventSubscription =>
    nativeModule?.addListener("onPlayRequest", listener) ?? noopUnsubscribe,

  addSearchRequestListener: (
    listener: (event: CarSearchRequest) => void,
  ): EventSubscription =>
    nativeModule?.addListener("onSearchRequest", listener) ?? noopUnsubscribe,

  /** Exists so DRM/playback failures reach Sentry; the head unit only shows a generic error. */
  addPlaybackErrorListener: (
    listener: (event: CarPlaybackError) => void,
  ): EventSubscription =>
    nativeModule?.addListener("onPlaybackError", listener) ?? noopUnsubscribe,
};

/** Whether to mount the `CarIntegration` glue at the app root. */
export const isCarIntegrationSupported = CustomPlatform.select<boolean>({
  ios: true,
  android: true,
  default: false,
});

/** `CustomPlatform` per ADR 0002: `Platform.OS === "ios"` would also match tvOS. */
export const isCarPlaySupported = CustomPlatform.select<boolean>({
  ios: true,
  default: false,
});

export default Car;
